import { useState, useContext } from 'react'
import {
  Button,
  Card,
  Grid,
  Heading,
  Input,
  Modal,
  Pagination,
  Spinner,
  Tooltip,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import {
  MANAGE_NOTIFICATION_EVENT_FIELDS,
  MANAGE_NOTIFICATION_RECORDS_FIELDS,
  TOASTER_DEFAULTS,
} from '../../globalConfig/constants'
import EnterpriseIcon, {
  BellIcon,
  PencilIcon,
  TrashIcon,
} from '@enterprise-ui/icons'
import {
  deleteAllNotifications,
  deleteNotification,
  resumeAllNotifications,
  resumeNotification,
} from '../../services/requests'
import { ManageNotificationsPauseModal } from './ManageNotificationsPauseModal'
import { NotificationCriteriaContext } from '../../globalState/ManageNotifications'

export const ManageNotificationsRecords = ({
  currentNotifications,
  updateCurrentNotifications,
  waitingOnGetNotifications,
  getNotifications,
  setIsUpdating,
  isUpdatingNotification,
  updatingNotificationId,
}) => {
  const makeToast = useToaster()
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [pausedNotificationId, setPausedNotificationId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteNotificationId, setDeleteNotificationId] = useState(null)
  const [notificationCriteriaState, notificationCriteriaDispatch] = useContext(
    NotificationCriteriaContext,
  )

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const currentItems = currentNotifications.slice(
    currentPage * itemsPerPage - itemsPerPage,
    currentPage * itemsPerPage,
  )
  const startingRange = (currentPage - 1) * itemsPerPage + 1
  const endingRange = Math.min(
    currentPage * itemsPerPage,
    currentNotifications.length,
  )
  const showingRecordsString = `Showing ${startingRange} - ${endingRange} of ${currentNotifications.length} records`

  const handleDeleteNotification = () => {
    const sendSuccessToast = (isAll) => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'success',
        heading: 'Delete Successful',
        message: isAll
          ? 'Successfully deleted all notifications.'
          : 'Successfully deleted notification.',
      })
    }
    const sendFailureToast = (isAll) => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: isAll
          ? 'Failed to delete all notifications.'
          : 'Failed to delete notification.',
      })
    }
    if (deleteNotificationId === null) {
      deleteAllNotifications()
        .then((response) => {
          if (response?.status === 200) {
            sendSuccessToast(true)
            getNotifications()
            setDeleteNotificationId(null)
          } else {
            sendFailureToast(true)
          }
        })
        .catch(() => {
          sendFailureToast(true)
        })
    } else {
      deleteNotification(deleteNotificationId)
        .then((response) => {
          if (response?.status === 200) {
            sendSuccessToast(false)
            getNotifications()
            setDeleteNotificationId(null)
          } else {
            sendFailureToast(false)
          }
        })
        .catch(() => {
          sendFailureToast(false)
        })
    }
  }

  const handleResumeNotification = (notificationId) => {
    const sendFailureToast = (isAll) => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: isAll
          ? 'Failed to resume all notifications.'
          : 'Failed to resume notification.',
      })
    }
    if (notificationId === null) {
      resumeAllNotifications()
        .then((response) => {
          if (response?.status === 200) {
            updateCurrentNotifications(response.data.notificationRules)
          } else {
            sendFailureToast(true)
          }
        })
        .catch(() => {
          sendFailureToast(true)
        })
    } else {
      resumeNotification(notificationId)
        .then((response) => {
          if (response?.status === 200) {
            updateCurrentNotifications(response.data)
          } else {
            sendFailureToast(false)
          }
        })
        .catch(() => {
          sendFailureToast(false)
        })
    }
  }

  const handleUpdateNotification = (notificationId) => {
    setIsUpdating(true, notificationId)

    const notificationToUpdate = currentNotifications.find(
      (notification) =>
        notification.notificationPreference.notificationId === notificationId,
    )

    if (notificationToUpdate) {
      const { notificationPreference, events } = notificationToUpdate

      const transformToAutocompleteFormat = (items) => {
        const item = items[0]
        return {
          id: item.value || item,
          value: item.value || item,
          label: item.value || item,
        }
      }

      const updatedCriteria = {
        vendorIds: transformToAutocompleteFormat(
          notificationPreference?.vendorIds || [],
        ),
        timezone: notificationPreference?.timezone || null,
      }

      const updatedEvents = {
        eventTypes: events.map((event) => event.eventType) || [],
      }

      notificationCriteriaDispatch({
        type: 'update_notifications_criteria',
        notificationCriteria: {
          ...notificationCriteriaState.notificationCriteria,
          ...updatedCriteria,
        },
      })

      notificationCriteriaDispatch({
        type: 'update_notification_events',
        notificationEvents: {
          ...notificationCriteriaState.notificationEvents,
          ...updatedEvents,
        },
      })
    }
  }

  const isNotificationPaused = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return false
    }

    const currentDate = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)

    return currentDate >= start && currentDate <= end
  }

  const areAllNotificationsPaused = () => {
    return currentNotifications.every((notification) =>
      isNotificationPaused(
        notification.notificationPreference.pauseStartDate,
        notification.notificationPreference.pauseEndDate,
      ),
    )
  }

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-')
    return `${month}/${day}/${year}`
  }

  return (
    <>
      <div
        className="hc-pt-normal hesp-general-flex hc-mb-normal"
        style={{ marginTop: '24px' }}
      >
        <Heading size={3}>Notifications</Heading>
        {currentNotifications.length !== 0 ? (
          <Grid.Container direction="row-reverse" spacing="dense">
            <Grid.Item>
              {' '}
              {areAllNotificationsPaused() ? (
                <Button
                  data-testid={'notification-resume-all-button'}
                  style={{ marginRight: '16px' }}
                  type="secondary"
                  onClick={() => handleResumeNotification(null)}
                  disabled={isUpdatingNotification}
                >
                  Resume All
                </Button>
              ) : (
                <Button
                  data-testid={'notification-pause-all-button'}
                  style={{ marginRight: '16px' }}
                  type="primary"
                  onClick={() => setShowPauseModal(true)}
                  disabled={isUpdatingNotification}
                >
                  Pause All
                </Button>
              )}{' '}
              <Button
                data-testid={'notification-delete-all-button'}
                type="destructive"
                onClick={() => setShowDeleteModal(true)}
                disabled={isUpdatingNotification}
              >
                Delete All
              </Button>
            </Grid.Item>
          </Grid.Container>
        ) : null}
      </div>
      <Grid.Container className="hc-pa-normal">
        <Grid.Item className="hesp-general-fullwidth hc-pa-none">
          <Card
            className="hc-pa-sm"
            style={{
              backgroundColor: 'var(--canvas-background-color--default)',
              height: 'calc(100vh - 500px)',
              minHeight: '400px',
              overflowY: 'scroll',
              position: 'relative',
            }}
          >
            {waitingOnGetNotifications ? (
              <div className="hesp-spinner-container">
                <Spinner data-testid="data-table-spinner" size="expanded" />
              </div>
            ) : currentNotifications.length !== 0 ? (
              <div className="hesp-general">
                {currentItems?.map((record, index) => (
                  <Card
                    key={'notification-record-' + index}
                    data-testid={'notification-record-' + index}
                    className={`hesp-notifications-cards ${
                      isUpdatingNotification &&
                      updatingNotificationId !==
                        record.notificationPreference.notificationId
                        ? 'dull'
                        : ''
                    }`}
                  >
                    <div
                      className="hc-pa-normal hesp-general-flex"
                      style={{ justifyContent: 'space-between' }}
                    >
                      <div>
                        {Object.values(MANAGE_NOTIFICATION_RECORDS_FIELDS).map(
                          (field) => {
                            const value =
                              record?.notificationPreference?.[
                                field.variableName
                              ]

                            return (
                              <div
                                key={field.variableName}
                                className="hesp-general-table-cell hesp-general-alignItemsCenter hesp-right-padding"
                                style={{ width: '130px', maxWidth: '130px' }}
                              >
                                <Heading size={5} className="hc-mr-lg">
                                  {field.label}
                                </Heading>
                                {value !== undefined && value?.length !== 0 ? (
                                  <span>
                                    <Tooltip
                                      className="hesp-tooltip"
                                      content={
                                        Array.isArray(value)
                                          ? value.join(', ')
                                          : value
                                      }
                                      location="bottom-left"
                                    >
                                      <p className="hesp-notifications-ellipsis">
                                        {Array.isArray(value)
                                          ? value.join(', ')
                                          : value}
                                      </p>
                                    </Tooltip>
                                  </span>
                                ) : null}
                              </div>
                            )
                          },
                        )}
                        <div
                          key={'events-' + index}
                          className="hesp-notifications-events-container"
                        >
                          <Heading size={5} className="hc-mr-sm">
                            Events
                          </Heading>
                          <span>
                            {record.events
                              .map((event) => {
                                const eventField = Object.values(
                                  MANAGE_NOTIFICATION_EVENT_FIELDS,
                                ).find(
                                  (field) =>
                                    field.variableName === event.eventType,
                                )
                                return eventField
                                  ? eventField.label
                                  : event.eventType
                              })
                              .join(', ')}
                          </span>
                        </div>
                      </div>
                      <div
                        className="hesp-general-justifyEnd hc-mr-sm"
                        data-testid={'notification-record-actions-' + index}
                        style={{ alignContent: 'center', position: 'relative' }}
                      >
                        <div className="hesp-notification-buttons">
                          {isNotificationPaused(
                            record?.notificationPreference?.pauseStartDate,
                            record?.notificationPreference?.pauseEndDate,
                          ) ? (
                            <Button
                              className="hc-mr-lg"
                              style={{ width: '105px' }}
                              data-testid={
                                'notification-resume-button-' + index
                              }
                              type="secondary"
                              onClick={() =>
                                handleResumeNotification(
                                  record?.notificationPreference
                                    ?.notificationId,
                                )
                              }
                              disabled={isUpdatingNotification}
                            >
                              Resume
                              <EnterpriseIcon
                                icon={BellIcon}
                                style={{ marginLeft: '4px' }}
                              ></EnterpriseIcon>
                            </Button>
                          ) : (
                            <Button
                              className="hc-mr-lg"
                              style={{ width: '105px' }}
                              data-testid={'notification-pause-button-' + index}
                              type="primary"
                              onClick={() => {
                                setPausedNotificationId(
                                  record?.notificationPreference
                                    ?.notificationId,
                                )
                                setShowPauseModal(true)
                              }}
                              disabled={isUpdatingNotification}
                            >
                              Pause
                              <EnterpriseIcon
                                icon={BellIcon}
                                style={{ marginLeft: '4px' }}
                              ></EnterpriseIcon>
                            </Button>
                          )}
                          <Button
                            className="hc-mr-lg"
                            data-testid={'notification-edit-button-' + index}
                            onClick={() =>
                              handleUpdateNotification(
                                record.notificationPreference.notificationId,
                              )
                            }
                            disabled={isUpdatingNotification}
                          >
                            Edit
                            <EnterpriseIcon
                              icon={PencilIcon}
                              style={{ marginLeft: '4px' }}
                            ></EnterpriseIcon>
                          </Button>
                          <Button
                            data-testid={'notification-delete-button-' + index}
                            type="destructive"
                            onClick={() => {
                              setDeleteNotificationId(
                                record?.notificationPreference?.notificationId,
                              )
                              setShowDeleteModal(true)
                            }}
                            disabled={isUpdatingNotification}
                          >
                            Delete
                            <EnterpriseIcon
                              icon={TrashIcon}
                              style={{ marginLeft: '4px' }}
                            ></EnterpriseIcon>
                          </Button>
                        </div>
                        {record?.notificationPreference?.pauseStartDate &&
                          record?.notificationPreference?.pauseEndDate && (
                            <span className="hesp-pause-info">
                              Paused from{' '}
                              {formatDate(
                                record?.notificationPreference?.pauseStartDate,
                              )}{' '}
                              to{' '}
                              {formatDate(
                                record?.notificationPreference?.pauseEndDate,
                              )}{' '}
                            </span>
                          )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '100%',
                  textAlign: 'center',
                  top: '200px',
                }}
              >
                {'No notifications currently set'}
              </span>
            )}
          </Card>
          {currentNotifications.length !== 0 ? (
            <div className="hesp-general-flex hesp-general-alignItemsCenter hc-mt-sm">
              <span className="hesp-general-flex-grow hesp-general-fullwidth">
                {showingRecordsString}
              </span>
              <Pagination
                className="hesp-general-flex-grow hesp-general-justifyCenter hesp-general-fullwidth"
                currentPage={currentPage}
                totalPages={Math.ceil(
                  currentNotifications.length / itemsPerPage,
                )}
                onRequestFirst={() => setCurrentPage(1)}
                onRequestNext={() => setCurrentPage(currentPage + 1)}
                onRequestPrev={() => setCurrentPage(currentPage - 1)}
                onRequestPage={(event, value) => setCurrentPage(value)}
                onRequestLast={() =>
                  setCurrentPage(
                    Math.ceil(currentNotifications.length / itemsPerPage),
                  )
                }
                pageSelector
              />
              <span className="hesp-general-flex hesp-general-alignItemsCenter hesp-general-justifyEnd hesp-general-flex-grow hesp-general-fullwidth">
                <span className="hc-mr-xs">Records per page</span>
                <Input.Select
                  size="dense"
                  value={itemsPerPage}
                  onUpdate={(id, value) => {
                    setItemsPerPage(value)
                  }}
                  options={[10, 25, 50].map((option) => {
                    return {
                      label: option,
                      value: option,
                    }
                  })}
                />
              </span>
            </div>
          ) : null}
        </Grid.Item>
      </Grid.Container>
      <ManageNotificationsPauseModal
        showModal={showPauseModal}
        onClose={() => {
          setShowPauseModal(false)
          setPausedNotificationId(null)
        }}
        updateCurrentNotifications={updateCurrentNotifications}
        notificationId={pausedNotificationId}
      ></ManageNotificationsPauseModal>
      <Modal
        data-testid="delete-notification-modal"
        headingText={
          deleteNotificationId === null
            ? 'Delete All Notifications'
            : 'Delete Notification'
        }
        isVisible={showDeleteModal}
        size="dense"
        onRefuse={() => {
          setShowDeleteModal(false)
          setDeleteNotificationId(null)
        }}
      >
        <div className="hc-pa-normal">
          <Grid.Container>
            <Grid.Item xs>
              <p>
                {deleteNotificationId === null
                  ? 'Are you sure you want to delete all your notifications?'
                  : 'Are you sure you want to delete this notification?'}
              </p>
            </Grid.Item>
          </Grid.Container>
          <Grid.Container direction="row-reverse" spacing="dense">
            <Grid.Item>
              <Button
                type="destructive"
                onClick={() => {
                  handleDeleteNotification()
                  setShowDeleteModal(false)
                }}
              >
                {deleteNotificationId === null ? 'Delete All' : 'Delete'}
              </Button>
            </Grid.Item>
            <Grid.Item>
              <Button
                type="secondary"
                onClick={() => {
                  setShowDeleteModal(false)
                }}
              >
                Cancel
              </Button>
            </Grid.Item>
          </Grid.Container>
        </div>
      </Modal>
    </>
  )
}
